/**
 * Alert <uo-tabs>
 *
 * Attributes:
 * parameter    The key of the parameter used to update the tab in the page URL
 * direction    Row or column, as with flex
 * order        The order parameter
 *
 * Methods:
 * showTab( tabId ) Select a tab
 */

import './panel';
import './tab';

// Import debounce
import debounce from 'debounce';

// Import utilities
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';

// Import styles used before the component loads
// This is used to prevent FOUC
import './fouc-fix.scss';

// Import style for web component
import styles from './web-component-style.scss?inline';

// Alert template
const template = document.createElement( 'template' );
template.innerHTML = `
	<style>
		
		${styles}

	</style>

	<div class="tabs">
		<div class="list"></div>

		<div class="panels">
			<slot></slot>
		</div>
	</div>
`;

/**
 * Creates <uo-tabs>
 */
class AutomatorTabs extends HTMLElement {
	/**
	 * Private property with the ID of the current selected tab
	 */
	#currentTab;

	/**
	 * Defines observed attributes
	 *
	 * @return {Array} Observed attributes
	 */
	static get observedAttributes() {
		return [ 'parameter', 'direction', 'order' ];
	}

	/**
	 * Creates the component
	 *
	 * @return {undefined}
	 */
	constructor() {
		super();

		// Attach shadow
		this.attachShadow( {
			mode: 'open',
		} );

		// Create alert
		this.shadowRoot.appendChild( template.content.cloneNode( true ) );

		// Move tabs
		// Iterate tabs
		this.querySelectorAll( ':scope > uo-tab' ).forEach( ( $tab ) => {
			// Move the child inside the list element
			this.$listWrapper.appendChild( $tab );
		} );

		// Order tabs after moving them
		this.#orderTabs();
	}

	/**
	 * Callback invoked when the component is added to the DOM
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		// Listen clicks on tabs
		this.#listenClicksToTabs();

		// Handle "column" tabs on small screens
		this.#columnDirectionOnSmallScreens();

		// Scroll to the selected tab
		this.#scrollToCurrentTab();
	}

	/**
	 * Callback invoked when the attribute changes
	 *
	 * @param {String} attrName Attribute name
	 * @param {String} oldValue The old value
	 * @param {String} newValue The new value
	 * @return {undefined}
	 */
	attributeChangedCallback( attrName, oldValue, newValue ) {
		// Check if the old value is different than the new one
		// This will prevent an infinite loop
		if ( oldValue !== newValue ) {
			this[attrName] = this.getAttribute( attrName );
		}
	}

	/**
	 * Returns the key of the URL parameter for the component
	 *
	 * @return {String} The parameter
	 */
	get parameter() {
		return this.getAttribute( 'parameter' );
	}

	/**
	 * Sets the parameter of the tabs
	 *
	 * @return {undefined}
	 */
	set parameter( value ) {
		// Check if the value exists
		if ( value ) {
			// Update the attribute
			this.setAttribute( 'parameter', value );
		} else {
			// Remove the attribute
			this.removeAttribute( 'parameter' );
		}
	}

	/**
	 * Returns the direction of the tabs
	 *
	 * @return {String} The direction
	 */
	get direction() {
		return this.getAttribute( 'direction' );
	}

	/**
	 * Sets the direction of the tabs
	 *
	 * @return {undefined}
	 */
	set direction( value ) {
		// Check if the value exists
		if ( value ) {
			// Update the attribute
			this.setAttribute( 'direction', value );

			// Update the attribute also in the .tabs element
			this.$tabsWrapper.setAttribute( 'direction', value );

			// Set the direction of the tabs
			this.#setDirectionOfTabs( value );
		} else {
			// Remove the attribute
			this.removeAttribute( 'direction' );

			// Remove the attribute also in the .tabs element
			this.$tabsWrapper.removeAttribute( 'direction' );

			// Reset the direction of the tabs
			this.#setDirectionOfTabs();
		}
	}

	/**
	 * Returns the order property of the tabs
	 *
	 * @return {String} The order property
	 */
	get order() {
		return this.getAttribute( 'order' );
	}

	/**
	 * Sets the order of the tabs
	 *
	 * @return {undefined}
	 */
	set order( value ) {
		// Check if the value exists
		if ( [ 'title' ].includes( value ) ) {
			// Update the attribute
			this.setAttribute( 'order', value );
		} else {
			// Remove the attribute
			this.removeAttribute( 'order' );
		}
	}

	/**
	 * Listen clicks to tabs
	 *
	 * @return {undefined}
	 */
	#listenClicksToTabs() {
		// Iterate tabs
		this.$tabs.forEach( ( $tab ) => {
			// Add event listener
			$tab.addEventListener( 'click', () => {
				// Check if the tab is NOT disabled
				if ( ! $tab.disabled ) {
					// Get ID of the tab
					const tabId = $tab.id;

					// Show tab
					this.showTab( tabId );
				}
			} );
		} );
	}

	/**
	 * Handle small screens when using the column view
	 *
	 * @return {undefined}
	 */
	#columnDirectionOnSmallScreens() {
		// Define the breakpoint
		const breakpoint = 750;

		// Check if it's using the column view
		if ( this.direction == 'column' ) {
			// Observer the wrapper width
			new ResizeObserver(
				// Debounce the event to improve performance
				debounce( ( event ) => {
					// Check if the width of the tabs wrapper is smaller than {breakpoint}
					if ( this.offsetWidth <= breakpoint ) {
						// Change the view automatically to direction
						this.direction = 'row';
					} else {
						// Revert the direction change
						this.direction = 'column';
					}
				}, 300 )
			).observe( this );
		}
	}

	/**
	 * Scrolls to the current tab.
	 * Context: Automator doesn't wrap the tabs, even if there isn't enough space.
	 * As a solution, it shows a scrollbar. This method scrolls to the tab position
	 * so the current tab is visible by default.
	 *
	 * @return {undefined}
	 */
	#scrollToCurrentTab() {
		// Get the tab and panel nodes of the selected node
		const { $tab, $panel } = this.getTabElementsById( this.currentTab );

		// Check if the tab element exists (not the panel)
		if ( isDefined( $tab ) ) {
			// Scroll to the tab
			setTimeout( () => {
				// Scroll to the tab
				$tab.scrollIntoView( { block: 'end' } );
			}, 500 );
		}
	}

	/**
	 * Shows a tab
	 *
	 * @param {String} tabId The tab ID
	 * @return {undefined}
	 */
	showTab( tabId ) {
		// Get the tab and panel nodes
		const { $tab, $panel } = this.getTabElementsById( tabId );

		// Check if the tab exists
		if ( isDefined( $tab ) ) {
			// Save the current tab ID
			this.currentTab = tabId;

			// Check if it has an href defined
			if ( isDefined( $tab.href ) ) {
				// Set the loading animation of the tab
				$tab.setAttribute( 'loading', '' );

				// Redirect to that href
				window.location.href = $tab.href;
			} else {
				// If it doesn't have an href, then maybe the content is already loaded
				// The next step is to find the panel of the tab
				// Check if the panel exists
				// eslint-disable-next-line no-lonely-if
				if ( isDefined( $panel ) ) {
					// Check if it has an parameter defined
					if ( ! isEmpty( this.parameter ) ) {
						// Parameters
						const parameters = {};

						// Add parameter
						parameters[this.parameter] = tabId;

						// Update the URL with the ID of the tab
						addParametersToURL( '', parameters, {
							setAsURL: true,
						} );
					}

					// Unselect all tabs
					this.$tabs.forEach( ( $item ) => {
						$item.removeAttribute( 'active' );
					} );

					// Unselect all panels
					this.$panels.forEach( ( $item ) => {
						$item.removeAttribute( 'active' );
					} );

					// Select correct tab
					$tab.setAttribute( 'active', '' );
					$panel.setAttribute( 'active', '' );
				} else {
					// Check if debugging mode is enabled
					// eslint-disable-next-line no-lonely-if
					if ( UncannyAutomatorBackend.debugging.enabled ) {
						// eslint-disable-next-line no-console
						console.warn( `uo-tabs couldn't find the tab panel "${tabId}"` );
					}
				}
			}
		} else {
			// Check if debugging mode is enabled
			// eslint-disable-next-line no-lonely-if
			if ( UncannyAutomatorBackend.debugging.enabled ) {
				// eslint-disable-next-line no-console
				console.warn( `uo-tabs couldn't find the tab "${tabId}"` );
			}
		}
	}

	/**
	 * Returns the tab and panel, given the tab ID
	 *
	 * @param {String} tabId The tab ID
	 * @return {Object}       An object with the tab and panel
	 */
	getTabElementsById( tabId ) {
		// Default response
		const response = {
			$tab: null,
			$panel: null,
		};

		// Get tab
		this.$tabs.forEach( ( $tab ) => {
			if ( $tab.id == tabId ) {
				response.$tab = $tab;
			}
		} );

		// Get panel
		this.$panels.forEach( ( $panel ) => {
			if ( $panel.id == tabId ) {
				response.$panel = $panel;
			}
		} );

		return response;
	}

	/**
	 * Sets the direction of the tabs
	 *
	 * @param {String} direction The tab direction
	 * @return {undefined}
	 */
	#setDirectionOfTabs( direction = 'row' ) {
		// Iterate tabs
		this.$tabs.forEach( ( $tab ) => {
			$tab.setAttribute( 'direction', direction );
		} );
	}

	/**
	 * Orders the tabs based on a property
	 * The order process is not reversible, and the original order will be lost
	 */
	#orderTabs() {
		// Check if the parameter has a value
		if ( ! isEmpty( this.order ) ) {
			// Create Array to save the ordered tabs
			let orderedTabs = [];

			// Check the criteria
			switch ( this.order ) {
				case 'title':
					// Iterate tabs
					orderedTabs = [ ...this.$tabs ].sort( ( a, b ) => {
						// Get the title of both tabs
						return a.innerText.localeCompare( b.innerText );
					} );

					break;
			}

			// Move the tabs
			orderedTabs.forEach( ( $tab ) => {
				this.$listWrapper.appendChild( $tab );
			} );
		}
	}

	/**
	 * Returns the current tab ID
	 *
	 * @return {String} The tab ID
	 */
	get currentTab() {
		let tab = this.#currentTab;

		// Check if it's empty and try to get it anyway
		if ( isEmpty( this.#currentTab ) ) {
			try {
				// Try to get the ID of the active tab
				const $selectedTab = [ ...this.$tabs ].filter( ( $tab ) => {
					return $tab.hasAttribute( 'active' );
				} );

				// If there isn't a tab selected, try to get the ID using the panel ID
				if ( ! isEmpty( $selectedTab ) ) {
					// Update the ID of the selected tab
					tab = $selectedTab[0].id;
				} else {
					// Try to get the ID of the active panel
					const $selectedPanel = [ ...this.$panels ].filter( ( $panel ) => {
						return $panel.hasAttribute( 'active' );
					} );

					if ( ! isEmpty( $selectedPanel ) ) {
						tab = $selectedPanel[0].id;
					}
				}
			} catch ( e ) {}
		}

		return tab;
	}

	/**
	 * Sets the current tab
	 *
	 * @param {String} tabId The tab ID
	 * @return {undefined}
	 */
	set currentTab( tabId ) {
		this.#currentTab = tabId;
	}

	/**
	 * Get tabs wrapper element
	 *
	 * @return {Node} The tabs wrapper element
	 */
	get $tabsWrapper() {
		return this.shadowRoot.querySelector( '.tabs' );
	}

	/**
	 * Get list element
	 *
	 * @return {Node} The list element
	 */
	get $listWrapper() {
		return this.shadowRoot.querySelector( '.list' );
	}

	/**
	 * Get panels element
	 *
	 * @return {Node} The panels element
	 */
	get $panelsWrapper() {
		return this.shadowRoot.querySelector( '.panels' );
	}

	/**
	 * Get tabs
	 *
	 * @return {NodeList} The tabs
	 */
	get $tabs() {
		return this.shadowRoot.querySelectorAll( 'uo-tab' );
	}

	/**
	 * Get panels
	 * These are in the Light DOM
	 *
	 * @return {NodeList} The panels
	 */
	get $panels() {
		return this.querySelectorAll( ':scope > uo-tab-panel' );
	}
}

customElements.define( 'uo-tabs', AutomatorTabs );
